import { useEffect, useState } from "react";

import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "@mui/material";
import { PageContainer } from "@/features/ui";

import { PurchaseOrderForm } from "@/features/purchase-orders";
import { apiOutletClient, getErrorMessage } from "@/features/shared";
import { CenteredLoading, useSnackbar } from "@/features/ui";

export const EditPurchaseOrder = () => {
  const { referenceNumber } = useParams();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);
  const [defaultValues, setDefaultValues] = useState(null);

  useEffect(() => {
    const fetchPurchaseOrder = async () => {
      setIsLoading(true);
      try {
        const {
          data: { data },
        } = await apiOutletClient.get(
          `purchase-orders/standard/${referenceNumber}`,
          {
            params: {
              withBuyer: true,
              withSeller: true,
              withWarehouse: true,
              withProductVariants: true,
              withPaymentTerm: true,
              withCreator: true,
            },
          }
        );

        setDefaultValues({
          taxable: data.taxable,
          totalIncludesTax: data.totalIncludesTax,
          transactionDate: dayjs(data.transactionDate),
          warehouse: data.warehouse,
          buyer: data.buyer,
          seller: data.seller,
          paymentTerm: data.paymentTerm,
          note: data.note,
          taxAmount: data.taxAmount,
          taxPercentage: data.taxPercentage,
          discountInput: data.discountAmount || 0,
          discountType: data.discountType,
          grandTotal: data.grandTotal,
          purchaseOrderProductVariants: data.purchaseOrderProductVariants.map(
            (variant) => ({
              id: variant.id,
              productVariantId: variant.productVariantId,
              name: variant.name,
              sku: variant.sku,
              unitOfMeasurement: variant.product.unitOfMeasurement.name,
              quantity: variant.quantity,
              receivedQuantity: variant.receivedQuantity,
              discount: variant.discount,
              costPerUnit: variant.costPerUnit,
              note: variant.note,
              salesRequestProductVariant: variant.salesRequestProductVariant,
            })
          ),
        });
      } catch (error) {
        showSnackbar(getErrorMessage(error));
      } finally {
        setIsLoading(false);
      }
    };
    fetchPurchaseOrder();
  }, [referenceNumber, showSnackbar]);

  const handleSubmit = async (formPayload) => {
    try {
      // The form already builds the full UpdateStandardPurchaseOrdersDto
      // payload (transaction date, warehouse, product variants, discount,
      // totals). Forward it as-is and only peel off buyerId for the header.
      const { buyerId, ...payload } = formPayload;

      await apiOutletClient.patch(
        `purchase-orders/standard/${referenceNumber}`,
        payload,
        {
          headers: {
            "Outlet-Id": buyerId,
          },
        }
      );

      showSnackbar("Pesanan pembelian berhasil diperbarui", "success");
      navigate(-1);
    } catch (error) {
      showSnackbar(getErrorMessage(error));
    }
  };

  if (!defaultValues && isLoading) {
    return <CenteredLoading />;
  }

  return (
    <PageContainer
      maxWidth="xl"
      title="Ubah Pesanan Pembelian"
      breadcrumbs={[
        {
          title: "Pesanan Pembelian",
          path: "/procurement/purchase-orders/standard",
        },
        {
          title: referenceNumber,
          path: `/procurement/purchase-orders/standard/${referenceNumber}`,
        },
        { title: "Ubah" },
      ]}
    >
      <PurchaseOrderForm
        editMode={true}
        isLoading={isLoading}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        renderActionButton={({ isSubmitting }) => (
          <Button type="submit" variant="contained" loading={isSubmitting}>
            Simpan
          </Button>
        )}
      />
    </PageContainer>
  );
};
